import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faCog,
  faEnvelopeOpen,
  faQuestionCircle,
  faSearch,
  faSignOutAlt,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { faUserCircle } from "@fortawesome/free-regular-svg-icons";
import {
  Row,
  Col,
  Nav,
  Form,
  Image,
  Navbar,
  Dropdown,
  Container,
  ListGroup,
  InputGroup,
  Button,
  Spinner,
} from "@themesberg/react-bootstrap";

import Profile3 from "../assets/img/team/profile-picture-3.jpg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Routes } from "../routes";
import api, {
  logout,
  getAllNotifications,
  getUnreadNotificationsCount,
  markNotificationRead,
  markAllNotificationsRead,
  globalSearch,
} from "../pages/api";
import { MEDIA_URL } from "../pages/helper";
import { toast } from "react-toastify";

const normalizeRole = (role) => String(role || "").trim().toUpperCase();

const getProfilePathByRole = (role) => {
  const normalizedRole = normalizeRole(role);

  switch (normalizedRole) {
    case "ADMIN":
    case "SUPER_ADMIN":
    case "ROLE_ADMIN":
    case "ROLE_SUPER_ADMIN":
      return "#";
    // return Routes.AdminProfilePage?.path || "/admin-profile-page";

    case "USER":
    case "ROLE_USER":
    case "CHILD":
    case "ROLE_CHILD":
      return Routes.UserProfilePage?.path || "/user-profile";
    default:
      return Routes.UserProfilePage?.path || "/user-profile";
  }
};

const getDashboardPathByRole = (role) => {
  const normalizedRole = normalizeRole(role);

  switch (normalizedRole) {
    case "ADMIN":
    case "SUPER_ADMIN":
    case "ROLE_ADMIN":
    case "ROLE_SUPER_ADMIN":
      return Routes.AdminDashboard?.path || "/admin-dashboard";

    case "USER":
    case "ROLE_USER":
    case "CHILD":
    case "ROLE_CHILD":
      return Routes?.UserDashboard?.path || "/user-dashboard";
    default:
      return Routes.UserDashboard?.path || "/user-dashboard";
  }
};

const getStoredUser = () => {
  try {
    const raw = sessionStorage.getItem("authUser");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const getProfilePhotoSrc = (photo) => {
  if (!photo) return "";

  const value = String(photo).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `${MEDIA_URL}/${value.replace(/^\/+/, "")}`.replace(/([^:]\/)\/+/g, "$1");
};

const mapNotificationItem = (item) => ({
  id: item?.id ?? Date.now(),
  title: item?.title || "Notification",
  message: item?.message || "",
  isRead: Boolean(item?.is_read),
  createdAt: item?.created_at || "",
});

const getSearchResultsPath = () => {
    return Routes?.SearchResults?.path || "/search-results";
  };

export default (props) => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAllNotifications, setShowAllNotifications] = useState(false);

  const location = useLocation();

  const [search, setSearch] = useState("");

  const searchDebounceRef = useRef(null);

  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   const q = params.get("search") || "";
  //   setSearch(q);
  // }, [location.search]);

  useEffect(() => {
  if (location.pathname !== getSearchResultsPath()) {
    setSearch("");

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  }
}, [location.pathname]);



  const handleGlobalSearch = async (value) => {
    setSearch(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(async () => {
      const keyword = value.trim();

      // Search cleared
      if (!keyword) {
        navigate(dashboardPath, { replace: true });
        return;
      }

      try {
        const response = await globalSearch(keyword);

        navigate(getSearchResultsPath(), {
          state: {
            keyword,
            data: response?.data?.data || {},
          },
        });
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Unable to search."
        );
      }
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const areNotificationsRead = unreadCount === 0;

  const fetchUnreadCount = async () => {
    try {
      const response = await getUnreadNotificationsCount();
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      setUnreadCount(Number(apiData?.unread_count || 0));
    } catch (error) {
      console.error("Failed to fetch unread notification count:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);

      const response = await getAllNotifications();
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiNotifications = Array.isArray(apiData?.notifications)
        ? apiData.notifications
        : [];

      setNotifications(apiNotifications.map(mapNotificationItem));
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();

    const intervalId = setInterval(() => {
      fetchUnreadCount();
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const [profilePhoto, setProfilePhoto] = useState(
    sessionStorage.getItem("authProfilePhoto") || ""
  );
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const updatePhoto = () => {
      setProfilePhoto(sessionStorage.getItem("authProfilePhoto") || "");
      setImgError(false);
    };

    window.addEventListener("authProfilePhotoUpdated", updatePhoto);
    window.addEventListener("storage", updatePhoto);

    return () => {
      window.removeEventListener("authProfilePhotoUpdated", updatePhoto);
      window.removeEventListener("storage", updatePhoto);
    };
  }, []);

  const handleLogout = async () => {
    const token = sessionStorage.getItem("authToken");
    const user = getStoredUser();
    const role = user?.user_type || sessionStorage.getItem("authRole") || "";

    try {
      if (token) {
        await logout({
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("authTokenType");
      sessionStorage.removeItem("authUser");
      sessionStorage.removeItem("authRole");
      sessionStorage.removeItem("isAuthenticated");
      sessionStorage.removeItem("authProfilePhoto");

      setProfilePhoto("");
      setImgError(false);

      navigate(Routes.Signin?.path || "/sign-in", { replace: true });
    }
  };

  const handleMarkSingleRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId ? { ...item, isRead: true } : item
        )
      );

      await fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();

      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const Notification = (props) => {
    const { id, title, message, createdAt, isRead = false } = props;
    const itemClassName = isRead ? "notification-read" : "notification-unread";

    return (
      <ListGroup.Item
        action
        as="button"
        type="button"
        className={`border-bottom border-light ${itemClassName} notification-item-btn`}
        onClick={() => handleMarkSingleRead(id)}
      >
        <Row className="align-items-start g-2">
          <Col className="col-auto">
            <div className="notification-avatar">
              <FontAwesomeIcon icon={faBell} />
            </div>
          </Col>
          <Col className="ps-0 ms--2">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="min-w-0">
                <h4 className="h6 mb-0 text-small text-break">{title}</h4>
              </div>
              <div className="text-end flex-shrink-0">
                <small className={isRead ? "text-muted" : "text-primary"}>
                  {createdAt}
                </small>
              </div>
            </div>
            <p className="font-small mt-1 mb-0 text-break">{message}</p>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  const storedUser = getStoredUser();
  const storedRole =
    storedUser?.user_type || sessionStorage.getItem("authRole") || "";
  const profilePath = getProfilePathByRole(storedRole);

  const getUserSupportPath = () => {
    return Routes?.UserSupportTicketList?.path || "/user-support-ticket-list";
  };

  const dashboardPath = getDashboardPathByRole(storedRole);
  const normalizedRole = String(storedRole || "").trim().toLowerCase();
  const isAdmin =
    normalizedRole === "admin" ||
    normalizedRole === "super_admin" ||
    normalizedRole === "role_admin" ||
    normalizedRole === "role_super_admin";

  const profilePhotoSrc = getProfilePhotoSrc(profilePhoto);

  const displayedNotifications = showAllNotifications
    ? notifications
    : notifications.slice(0, 5);

  const hasUnreadNotifications = notifications.some(
    (notification) => !notification.isRead
  );

  return (
    <>
      <Navbar variant="dark" expanded className="ps-0 pe-2 pb-0">
        <Container fluid className="px-0">
          <div className="d-flex justify-content-between w-100">
            <div className="d-flex align-items-center">
              <Form className="navbar-search">
                <Form.Group id="topbarSearch">
                  <InputGroup className="input-group-merge search-bar">
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    {/* <Form.Control type="text" placeholder="Search" /> */}
                    <Form.Control
                      type="text"
                      placeholder="Search"
                      value={search}
                      autoComplete="off"
                      onChange={(e) => handleGlobalSearch(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Form>
            </div>
            <Nav className="align-items-center">
              <Dropdown
                as={Nav.Item}
                autoClose="outside"
                onToggle={(nextShow) => {
                  if (nextShow) {
                    setShowAllNotifications(false);
                    fetchNotifications();
                    fetchUnreadCount();
                  }
                }}
              >
                <Dropdown.Toggle
                  as={Nav.Link}
                  className="text-dark icon-notifications me-lg-3"
                >
                  <span className="icon icon-sm position-relative">
                    <FontAwesomeIcon icon={faBell} className="bell-shake" />
                    {!areNotificationsRead ? (
                      <span className="notifications-count-badge">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    ) : null}
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu
                  align="end"
                  className="dashboard-dropdown notifications-dropdown dropdown-menu-lg mt-2 py-0"
                >
                  <ListGroup className="list-group-flush">
                    <Nav.Link
                      href="#"
                      className="text-center text-primary fw-bold border-bottom border-light py-3"
                      onClick={(e) => e.preventDefault()}
                    >
                      Notifications
                    </Nav.Link>

                    {notificationsLoading ? (
                      <div className="py-4 text-center text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading notifications...
                      </div>
                    ) : displayedNotifications.length > 0 ? (
                      displayedNotifications.map((n) => (
                        <Notification key={`notification-${n.id}`} {...n} />
                      ))
                    ) : (
                      <div className="py-4 text-center text-muted">
                        No notifications found.
                      </div>
                    )}

                    {notifications.length > 5 ? (
                      <>
                        {showAllNotifications && hasUnreadNotifications ? (
                          <Dropdown.Item
                            as="button"
                            type="button"
                            className="text-center text-primary fw-bold py-3 notification-action-btn"
                            onClick={handleMarkAllRead}
                          >
                            Mark all as read
                          </Dropdown.Item>
                        ) : null}

                        <Dropdown.Item
                          as="button"
                          type="button"
                          className="text-center text-primary fw-bold py-3 notification-action-btn"
                          onClick={() => setShowAllNotifications((prev) => !prev)}
                        >
                          {showAllNotifications ? "Show less" : "View all"}
                        </Dropdown.Item>
                      </>
                    ) : null}
                  </ListGroup>
                </Dropdown.Menu>
              </Dropdown>

              {/* Remove later */}
              {/* <div className="position-relative me-4" style={{ cursor: "default" }}>
                <span className="icon icon-sm">
                  <FontAwesomeIcon icon={faBell} className="bell-shake" />
                  {areNotificationsRead ? null : (
                    <span className="icon-badge rounded-circle unread-notifications" style={{ top: "-7px", right: "0px" }} />
                  )}
                </span>
              </div> */}
              {/* Remove later /*/}

              <Dropdown as={Nav.Item}>
                <Dropdown.Toggle as={Nav.Link} className="pt-1 px-0">
                  <div className="media d-flex align-items-center">
                    {profilePhotoSrc && !imgError ? (
                      <Image
                        src={profilePhotoSrc}
                        className="user-avatar md-avatar rounded-circle"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div
                        className="user-avatar md-avatar rounded-circle d-flex align-items-center justify-content-center bg-light"
                        style={{
                          width: "40px",
                          height: "40px",
                          color: "#9ca3af",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faUserCircle}
                          style={{ fontSize: "1.6rem" }}
                        />
                      </div>
                    )}
                    {/* <Image src={Profile3} className="user-avatar md-avatar rounded-circle" />
                  <div className="media-body ms-2 text-dark align-items-center d-none d-lg-block">
                    <span className="mb-0 font-small fw-bold">Bonnie Green</span>
                  </div> */}
                  </div>
                </Dropdown.Toggle>

                {/* Remove later */}
                <Dropdown.Menu className="user-dropdown dropdown-menu-right mt-2 py-1">
                  {!isAdmin && (
                    <>
                      <Dropdown.Item as={Link} to={profilePath} className="fw-bold">
                        <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                        My Profile
                      </Dropdown.Item>

                      {/* <Dropdown.Item
                        as={Link}
                        to={getUserSupportPath()}
                        className="fw-bold"
                      >
                        <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                        Support
                      </Dropdown.Item> */}

                      <Dropdown.Divider className="my-1" />
                    </>
                  )}

                  <Dropdown.Item className="fw-bold" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="text-danger me-2" />
                    Logout
                  </Dropdown.Item>
                  {/* Remove later /*/}
                </Dropdown.Menu>
                {/* <Dropdown.Menu className="user-dropdown dropdown-menu-right mt-2"> */}
                {/* <Dropdown.Item as={Link} to={profilePath} className="fw-bold">
                  <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                  My Profile
                </Dropdown.Item> */}

                {/* ============ */}
                {/* <Dropdown.Item className="fw-bold">
                  <FontAwesomeIcon icon={faCog} className="me-2" /> Settings
                </Dropdown.Item>
                <Dropdown.Item className="fw-bold">
                  <FontAwesomeIcon icon={faEnvelopeOpen} className="me-2" /> Messages
                </Dropdown.Item>
                <Dropdown.Item className="fw-bold">
                  <FontAwesomeIcon icon={faUserShield} className="me-2" /> Support
                </Dropdown.Item> */}

                {/* ========== */}

                {/* <Dropdown.Divider />

                  <Dropdown.Item className="fw-bold" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="text-danger me-2" /> Logout
                  </Dropdown.Item>
                </Dropdown.Menu> */}
              </Dropdown>
            </Nav>
          </div>
        </Container>
      </Navbar>

      <style>{`
        .notifications-dropdown {
          max-height: calc(100vh - 90px);
          overflow-y: auto;
          overflow-x: hidden;
          width: min(420px, calc(100vw - 24px));
          max-width: calc(100vw - 24px);
          right: -3rem !important;
          left: auto !important;
        }

        .notifications-count-badge {
          position: absolute;
          top: -13px;
          right: 7px;
          min-width: 19px;
          height: 19px;
          padding: 0 5px;
          border-radius: 999px;
          background: #ef4444;
          color: #fff;
          font-size: 9px !important;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          box-shadow: 0 0 0 2px #fff;
        }

        .notification-read {
          background: #ffffff;
        }

        .notification-unread {
          background: #f3f8ff;
        }

        .notification-item-btn {
          width: 100%;
          text-align: left;
          cursor: pointer;
        }

        .notification-item-btn:focus {
          box-shadow: none;
        }

        .notification-action-btn {
          white-space: nowrap;
        }

        .notification-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: rgba(44, 96, 186, 0.12);
          color: #2c60ba;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
          flex-shrink: 0;
        }

        .user-dropdown {
          min-width: 220px;
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
        }

        .user-dropdown .dropdown-item {
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
        }

        .user-dropdown .dropdown-divider {
          margin: 0.25rem 0;
        }

        .navbar-expand .navbar-nav .nav-link {
    padding-right: 0.2rem;
}

        @media (max-width: 576px) {
          .notifications-dropdown {
            width: calc(100vw - 24px);
          }

          .notifications-count-badge {
            top: -13px;
            right: 7px;
          }

          .notification-action-btn {
            text-align: center;
          }
        }
      `}</style>
    </>
  );
};